import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { filter, map, startWith, take } from 'rxjs';

import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';

import { ChatThread, ChatThreadSearchRequest } from '@lib/chat/models/chat.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { ChatInfoBoardComponent } from './components/chat-info-board/chat-info-board.component';
import { ChatMessagesListComponent } from './components/chat-messages-list/chat-messages-list.component';
import { ChatTabsComponent } from './components/chat-tabs/chat-tabs.component';
import { ChatThreadsListComponent } from './components/chat-threads-list/chat-threads-list.component';
import { ChatCommunicationService } from './core/services/chat-communication.service';
import { ChatNavigationService } from './core/services/chat-navigation.service';
import { ChatNotificationService } from './core/services/chat-notification.service';
import { ChatStateService } from './core/services/chat-state.service';

@Component({
  selector: 'chat-root',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    LoaderComponent,
    ChatTabsComponent,
    ChatMessagesListComponent,
    ChatInfoBoardComponent,
    ChatThreadsListComponent,
    MatFormField,
    MatLabel,
    MatPrefix,
    MatIcon,
    MatInput,
    TranslatePipe,
  ],
  templateUrl: './chat.component.html',
  styles: `
    :host {
      @apply h-full;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly chatState = inject(ChatStateService);
  private readonly navigationService = inject(ChatNavigationService);
  private readonly notificationService = inject(ChatNotificationService);
  private readonly chatCommunication = inject(ChatCommunicationService);

  protected readonly initialThreadsLoading = computed<boolean>(() => this.chatState.initialThreadsLoading());
  protected readonly hasSelectedThread = computed<boolean>(() => !!this.chatState.selectedThread());

  protected readonly myChatsCount = computed(() => this.chatState.myChatsCount());
  protected readonly inQueueCount = computed(() => this.chatState.inQueueCount());

  private readonly currentThreadId = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
      startWith(this.router.url),
      map(url => this.extractThreadIdFromUrl(url))
    ),
    { initialValue: null }
  );

  private readonly threadIdEffect = effect(() => {
    if (this.currentThreadId()) {
      this.chatState.clearSelectedThread();
      this.chatState.loadThreadData(this.currentThreadId());
    } else this.chatState.clearSelectedThread();
  });

  private readonly navigationEffect = effect(() => {
    const targetThreadId = this.navigationService.targetThreadId();

    if (targetThreadId && targetThreadId !== this.currentThreadId()) {
      const currentParams = this.route.snapshot.queryParams;
      const queryParams: any = { boardTab: 'profile' };

      if (currentParams['chatTab']) queryParams.chatTab = currentParams['chatTab'];

      this.router
        .navigate([targetThreadId, 'messages'], {
          relativeTo: this.route,
          queryParams,
          queryParamsHandling: 'merge',
        })
        .then(() => this.navigationService.clearTargetThread());
    }
  });

  ngOnInit() {
    this.chatState.init();
    this.initializeNotifications();
    this.resolveUserThread();
  }

  /**
   * Resolves a `userId` query param to that user's thread and redirects to it.
   * Entry point for deep-links that know the user but not the thread.
   */
  private resolveUserThread(): void {
    const userId = this.route.snapshot.queryParams['userId'];

    if (!userId || this.currentThreadId()) return;

    this.chatCommunication
      .searchThreads({ pageSize: 100 })
      .pipe(take(1))
      .subscribe({
        next: response => {
          const thread = response?.value.find(t => t.userIds?.includes(userId));

          if (thread) {
            this.router.navigate([thread.id, 'messages'], {
              relativeTo: this.route,
              queryParams: { boardTab: 'profile' },
            });
          } else {
            this.router.navigate([], { relativeTo: this.route, queryParams: {} });
          }
        },
        error: () => this.router.navigate([], { relativeTo: this.route, queryParams: {} }),
      });
  }

  protected onOpenThread($event: ChatThread) {
    const currentParams = this.route.snapshot.queryParams;
    const queryParams: any = { boardTab: 'profile' };

    if (currentParams['chatTab']) queryParams.chatTab = currentParams['chatTab'];

    this.router.navigate([$event.id, 'messages'], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  protected onSearchThreads($event: ChatThreadSearchRequest) {
    this.chatState.onTabFilterChange($event);
  }

  protected onSearchInput($event: Event) {
    const target = $event.target as HTMLInputElement;

    this.chatState.onThreadSearch(target.value);
  }

  protected readonly isThreadAssignedToMe = computed(() => {
    const selectedThread = this.chatState.selectedThread();
    const ownUserId = this.chatState.ownUserId();

    if (!selectedThread || !ownUserId) return false;
    else return selectedThread.assignees?.includes(ownUserId) || false;
  });

  protected onTakeIntoWork() {
    const selectedThread = this.chatState.selectedThread();
    const currentUserId = this.chatState.ownUserId();

    if (selectedThread && currentUserId) this.chatState.assignThread(selectedThread.id, currentUserId).subscribe();
  }

  protected onCompleteWork() {
    const selectedThread = this.chatState.selectedThread();

    if (selectedThread) this.chatState.unassignThread(selectedThread.id).subscribe();
  }

  private extractThreadIdFromUrl(url: string): string | null {
    const urlParts = url.split('?')[0].split('/'); // Remove query params and split by /
    const threadsIndex = urlParts.findIndex(part => part === 'threads');

    return threadsIndex >= 0 && urlParts[threadsIndex + 1] ? urlParts[threadsIndex + 1] : null;
  }

  private async initializeNotifications() {
    try {
      const permission = this.notificationService.permissionStatus();

      if (permission === 'default') console.log('Notification permission not requested yet. User can enable in settings.');
      if (permission === 'granted') console.log('Notifications are enabled');
    } catch (error) {
      console.error('Error initializing notifications:', error);
    }
  }
}
